<template>
  <v-container class="py-6" max-width="1000">
    <!-- Header -->
    <div class="d-flex align-center mb-6">
      <v-btn variant="text" size="small" prepend-icon="mdi-arrow-left" to="/admin/training" class="mr-3">
        Back
      </v-btn>
      <v-icon color="primary" size="28" class="mr-3">mdi-motion-play-outline</v-icon>
      <div>
        <h1 class="text-h5 font-weight-bold mb-0">Dice Analysis</h1>
        <p class="text-caption text-medium-emphasis mb-0">Run detection pipeline on a video</p>
      </div>
    </div>

    <!-- Start new job -->
    <v-card variant="outlined" class="mb-6">
      <v-card-title class="d-flex align-center pa-4 pb-2">
        <v-icon class="mr-2" color="primary">mdi-play-circle-outline</v-icon>
        New Analysis Job
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4">
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.videoId"
              :items="videos"
              item-title="label"
              item-value="videoId"
              label="Select video from library"
              variant="outlined"
              density="compact"
              clearable
              :disabled="!!form.videoPath"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.videoPath"
              label="Or enter video path directly"
              variant="outlined"
              density="compact"
              clearable
              :disabled="!!form.videoId"
              placeholder="/path/to/video.mp4"
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field
              v-model.number="form.frameSkip"
              label="Frame skip"
              type="number"
              variant="outlined"
              density="compact"
              :min="1"
              :max="10"
              hint="Process every N frames"
              persistent-hint
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field
              v-model.number="form.conf"
              label="Confidence threshold"
              type="number"
              variant="outlined"
              density="compact"
              :min="0.1"
              :max="0.9"
              :step="0.05"
              hint="Min detection confidence"
              persistent-hint
            />
          </v-col>
        </v-row>

        <v-alert v-if="startError" type="error" variant="tonal" density="compact" class="mt-3">
          {{ startError }}
        </v-alert>

        <div class="mt-4">
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-play"
            :loading="starting"
            :disabled="!form.videoId && !form.videoPath"
            @click="startJob"
          >
            Start Analysis
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Active job -->
    <v-card v-if="activeJob" variant="outlined" class="mb-6">
      <v-card-title class="d-flex align-center pa-4 pb-2">
        <v-icon class="mr-2" :color="jobStatusColor(activeJob.status)">
          {{ jobStatusIcon(activeJob.status) }}
        </v-icon>
        Job: {{ activeJob.jobId }}
        <v-chip size="small" :color="jobStatusColor(activeJob.status)" variant="tonal" class="ml-2">
          {{ activeJob.status }}
        </v-chip>
        <v-spacer />
        <v-btn
          v-if="activeJob.status === 'processing'"
          icon="mdi-refresh"
          size="small"
          variant="text"
          :loading="polling"
          @click="pollJob()"
        />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4">
        <p class="text-caption text-medium-emphasis mb-2">
          {{ path.basename(activeJob.videoPath || '') }} — started {{ formatDate(activeJob.startedAt) }}
        </p>

        <div class="log-box mb-4">
          <div v-for="(line, i) in activeJob.log" :key="i" class="log-line">{{ line }}</div>
        </div>

        <template v-if="activeJob.status === 'done' && activeJob.result">
          <v-divider class="mb-4" />
          <ResultSummary :result="activeJob.result" :jobId="activeJob.jobId" />
        </template>

        <v-alert v-if="activeJob.error" type="error" variant="tonal" density="compact">
          {{ activeJob.error }}
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Previous jobs -->
    <v-card variant="outlined" v-if="previousJobs.length">
      <v-card-title class="pa-4 pb-2 d-flex align-center">
        <v-icon class="mr-2">mdi-history</v-icon>
        Previous Jobs
      </v-card-title>
      <v-divider />
      <v-table density="comfortable">
        <thead>
          <tr>
            <th>Video</th>
            <th>Status</th>
            <th>Started</th>
            <th>Rolls</th>
            <th>Crops</th>
            <th>Reviewed</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="job in previousJobs" :key="job.jobId">
            <td class="text-caption">{{ job.videoPath }}</td>
            <td>
              <v-chip size="x-small" :color="jobStatusColor(job.status)" variant="tonal">
                {{ job.status }}
              </v-chip>
            </td>
            <td class="text-caption">{{ formatDate(job.startedAt) }}</td>
            <td class="text-caption">{{ job.totalRolls ?? '—' }}</td>
            <td class="text-caption">{{ job.cropsSaved ?? '—' }}</td>
            <td>
              <span v-if="job.cropsSaved" class="text-caption">
                {{ job.cropsReviewed ?? 0 }}/{{ job.cropsSaved }}
                <v-progress-linear
                  :model-value="job.cropsSaved ? ((job.cropsReviewed ?? 0) / job.cropsSaved) * 100 : 0"
                  color="success"
                  height="4"
                  rounded
                  style="width: 60px; display: inline-flex;"
                />
              </span>
              <span v-else class="text-caption text-disabled">—</span>
            </td>
            <td>
              <div class="d-flex gap-1 justify-end">
                <!-- View job details -->
                <v-btn
                  size="x-small"
                  variant="text"
                  @click="viewJob(job.jobId)"
                >
                  View
                </v-btn>

                <!-- Import crops to DB -->
                <v-btn
                  v-if="job.cropsSaved && !job.cropsImported"
                  size="x-small"
                  variant="text"
                  color="warning"
                  :loading="importingJob === job.jobId"
                  @click="importCrops(job)"
                  title="Import crops to database"
                >
                  Import
                </v-btn>

                <!-- Review pip values -->
                <v-btn
                  v-if="job.cropsSaved"
                  size="x-small"
                  variant="text"
                  color="primary"
                  :to="`/admin/analysis/${job.jobId}/review`"
                >
                  Review
                </v-btn>

                <!-- Reconstruct game -->
                <v-btn
                  v-if="job.status === 'done'"
                  size="x-small"
                  variant="text"
                  color="success"
                  :to="`/admin/analysis/${job.jobId}/reconstruct`"
                >
                  Reconstruct
                </v-btn>
              </div>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <!-- Import feedback snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import api from '../../services/api'
import ResultSummary from '../../components/analysis/ResultSummary.vue'

const path = { basename: (p = '') => p.split('/').pop() || p }

const videos       = ref([])
const activeJob    = ref(null)
const previousJobs = ref([])
const starting     = ref(false)
const polling      = ref(false)
const startError   = ref('')
const importingJob = ref(null)
const snackbar     = ref({ show: false, text: '', color: 'success' })
let pollTimer      = null

const form = ref({
  videoId:   null,
  videoPath: '',
  frameSkip: 2,
  conf:      0.35,
})

async function loadVideos() {
  try {
    const { data } = await api.get('/training/videos')
    videos.value = (data.videos || []).map(v => ({
      ...v,
      label: `#${v.videoId} ${v.originalName} (${v.frameCount} frames)`,
    }))
  } catch {}
}

async function startJob() {
  startError.value = ''
  starting.value = true
  try {
    const payload = { frameSkip: form.value.frameSkip, conf: form.value.conf }
    if (form.value.videoId) payload.videoId = form.value.videoId
    else payload.videoPath = form.value.videoPath
    const { data } = await api.post('/training/videos/analyze', payload)
    activeJob.value = { ...data, log: [], status: 'processing' }
    startPolling(data.jobId)
    await loadJobs()
  } catch (e) {
    startError.value = e?.response?.data?.error || 'Failed to start job'
  } finally {
    starting.value = false
  }
}

function startPolling(jobId) {
  stopPolling()
  pollTimer = setInterval(() => pollJob(jobId), 3000)
}
function stopPolling() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
}

async function pollJob(jobId) {
  const id = jobId || activeJob.value?.jobId
  if (!id) return
  polling.value = true
  try {
    const { data } = await api.get(`/training/videos/analyze?jobId=${id}`)
    activeJob.value = data
    if (data.status === 'done' || data.status === 'error') {
      stopPolling()
      await loadJobs()
    }
  } catch {} finally {
    polling.value = false
  }
}

async function viewJob(jobId) {
  await pollJob(jobId)
}

async function loadJobs() {
  try {
    const { data } = await api.get('/training/videos/analyze')
    previousJobs.value = data.jobs || []
  } catch {}
}

async function importCrops(job) {
  importingJob.value = job.jobId
  try {
    const { data } = await api.post(
      `/training/videos/analyze/crops?jobId=${job.jobId}`
    )
    snackbar.value = {
      show:  true,
      text:  `Imported ${data.imported} crops to database`,
      color: 'success',
    }
    // Mark as imported locally so button disappears
    job.cropsImported = true
    await loadJobs()
  } catch (e) {
    snackbar.value = {
      show:  true,
      text:  e?.response?.data?.error || 'Import failed',
      color: 'error',
    }
  } finally {
    importingJob.value = null
  }
}

function jobStatusColor(s) {
  return { queued: 'grey', processing: 'warning', done: 'success', error: 'error' }[s] || 'grey'
}
function jobStatusIcon(s) {
  return {
    queued:     'mdi-clock-outline',
    processing: 'mdi-progress-clock',
    done:       'mdi-check-circle',
    error:      'mdi-alert-circle',
  }[s] || 'mdi-circle'
}
function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleString('da-DK', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
  })
}

onMounted(() => { loadVideos(); loadJobs() })
onUnmounted(() => stopPolling())
</script>

<style scoped>
.log-box {
  background: rgba(0,0,0,0.3);
  border-radius: 6px;
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;
  font-family: monospace;
}
.log-line {
  font-size: 0.72rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.7);
  white-space: pre-wrap;
}
</style>